import { dialogMgr } from 'widgets/toolbox/dialogMgr';
import { getContentByUrl, getJSONByUrl } from 'widgets/toolbox/ajax';
import { render } from 'widgets/toolbox/render';
import { TEmitBusEvent } from 'widgets/global/EmitBusEvent';

/**
 * @description Base GlobalFlyout implementation
 * @param Modal Base widget for extending
 * @returns GlobalFlyout class
 */
function GlobalFlyoutClassCreator(Modal: import('widgets/global/Modal').TModal) {
    /**
     * @category widgets
     * @subcategory global
     * @class GlobalFlyout
     * @augments Modal
     * @classdesc Global Flyout component. Allows different flyouts to be triggered by firing an event {@link module#dialog:flyoutshow}.
     * <br>Was implemented to get rid from excessive duplicated popups markup in product lists etc.
     * <br>Also allow standalone components/link to trigger flyout by using {@link EmitBusEvent} widget.
     * <br>In general could be triggered either programmatically by firing an event, or using {@link EmitBusEvent} widget in DOM markup.
     * <br>Allows: add classes to container; render flyout heading; render flyout action buttons; render content either via URL, or from string.
     * @property {string} data-widget - Widget name `globalFlyout`
     * @property {string} data-disable-rendering - property should be set to `false` to allow rendering inside popup template.
     *
     * @example <caption>Global Flyout widget triggering via DOM</caption>
     * <button
     *     class="b-promotion-details"
     *     type="button"
     *     aria-describedby="promotions-${promotion.id}"
     *     data-widget="emitBusEvent"
     *     data-bus-event-type="flyoutshow"
     *     data-event-click.prevent="emitBusEvent"
     *     data-tau="promotion_details_cta"
     *     data-flyout-config='{
     *         "content": "${promotion.detailsJSONSafe}",
     *         "attributes": {
     *             "data-tau": "promotion_details_flyout"
     *         },
     *         "ariaLabel": "${Resource.msg('promotion.details.label','product',null)}"
     *     }'
     * >
     *     ${Resource.msg('promotion.details','product',null)}
     * </button>
     *
     * @example <caption>Global Flyout widget triggering via code in {@link ProductTile} widget (synchronous operation)</caption>
     * ...
     * showQuickView(button) {
     *     this.eventBus().emit('flyoutshow', {
     *         url: button.data('url'),
     *         wrapperClasses: 'm-quick_view',
     *         ariaLabel: this.prefs().productName,
     *         attributes: {
     *             'data-tau-unique': 'quick_view_flyout'
     *         }
     *     }, this.onModalShow.bind(this));
     * }
     */
    class GlobalFlyout extends Modal {
        wrapperClasses?: string | Array<string>;

        onAfterCloseListener?: (globalFlyout: this) => void;

        onCancelListener?: (globalFlyout: this) => void;

        /**
         * @description Widget initialization logic
         * @listens module:events#flyoutshow
         * @listens module:events#closeFlyout
         */
        init() {
            super.init();
            this.eventBus().on('flyoutshow', 'showFlyout');
            this.eventBus().on('flyoutclose', 'closeFlyout');
        }

        /**
         * @description Shows flyout. Depending on needs, content for flyout could be loaded via URL, or fetched from parameters.
         * Input parameter also contains setup for flyout, like container classes, action buttons, heading text etc.
         * @example <caption>An object with expected parameters</caption>
         * {
         *      showActions: true,
         *      headerText: 'Header text',
         *      actions: [{
         *          text: 'Cancel',
         *          handler: 'cancel',
         *          classes: 'm-outline'
         *      },{
         *          text: 'Confirm',
         *          handler: 'cancel',
         *          lastFocusElement: true
         *      }],
         *      wrapperClasses: 'class1 class2',
         *      ariaLabel: 'test aria label',
         *      url: 'http://www.google.com',
         *      contentType: 'json'
         * }
         * In case if input parameter is an instance of {@link EmitBusEvent},
         * flyout setup properties will be taken from `data-flyout-config`
         * attribute of a {@link EmitBusEvent} widget.
         * @emits GlobalFlyout#show
         * @param templateData Input object for flyout popup. See example above.
         * @param cb - Callback function
         * @returns Promise object represents flyout rendering result
         */
        showFlyout(templateData?: Record<string, unknown> | InstanceType<TEmitBusEvent>, cb?: () => void): Promise<void> {
            let flyoutData: {
                content?: string;
                url?: string;
                contentType?: string;
                wrapperClasses?: Array<string> | string;
                bodyClass?: string;
                trigger?: Node;
                contentWrapper?: string;
                onAfterClose?: (globalFlyout: GlobalFlyout) => void;
                onCancel?: (globalFlyout: GlobalFlyout) => void;
            };

            const EmitBusEventClass = <TEmitBusEvent> this.getConstructor('emitBusEvent');

            if (templateData instanceof EmitBusEventClass) {
                flyoutData = {
                    url: templateData.data<string>('url')
                };
                const flyoutConfig = templateData.data<Record<string, unknown>>('flyoutConfig');

                if (flyoutConfig && Object.keys(flyoutConfig).length) {
                    flyoutData = Object.assign(flyoutData, flyoutConfig);
                }
            } else {
                flyoutData = templateData || {};
            }

            this.onCancelListener = flyoutData.onCancel;
            this.onAfterCloseListener = flyoutData.onAfterClose;

            this.wrapperClasses = flyoutData.wrapperClasses;

            if (flyoutData.bodyClass) {
                this.classesGlobalDialog = flyoutData.bodyClass;
            }

            this.beforeOpenFlyout(flyoutData);

            if (flyoutData.content) {
                return super.showModal(flyoutData, cb).then(() => {
                    if (this.wrapperClasses) {
                        this.ref(this.prefs().refContainer).addClass(this.wrapperClasses);
                    }
                }).finally(() => {
                    /**
                     * @description Event dispatched, when Global Flyout was shown
                     * @event GlobalFlyout#show
                     */
                    this.emit('show');
                });
            }

            if (!flyoutData.url) {
                return Promise.resolve();
            }

            super.showModalPreview(flyoutData.wrapperClasses);

            return this.getFlyoutContent(flyoutData).then(
                (content) => this.showFlyoutOnResponse(content, flyoutData, cb)
            );
        }

        /**
         * @description Get flyout data from url
         * @param flyoutData - flyout data
         * @returns Promise object represents flyout rendering result
         */
        getFlyoutContent(flyoutData) {
            if (flyoutData.contentType === 'json') {
                return getJSONByUrl(flyoutData.url, undefined, true);
            } else {
                return getContentByUrl(flyoutData.url).then(res => {
                    if (flyoutData.contentWrapper) {
                        return render(flyoutData.contentWrapper, Object.assign(flyoutData, { content: res }));
                    }

                    return Promise.resolve(res);
                });
            }
        }

        /**
         * @description process response data and pass it to flyout
         * @param response - response object
         * @param flyoutData - flyout data
         * @param cb - callback function
         * @returns Promise object represents flyout rendering result
         */
        showFlyoutOnResponse(response, flyoutData, cb) {
            if (Object.keys(response).length) {
                flyoutData = Object.assign(flyoutData, { content: response });
                const promise = super.showModal(flyoutData, cb, Boolean(flyoutData.url));

                /**
                 * @description Event dispatched, when Global Flyout was shown
                 * @event GlobalFlyout#show
                 */
                this.emit('show');

                return promise;
            }

            return Promise.resolve();
        }

        /**
         * @description Removes wrap classes from flyout
         */
        removeWrapperClasses() {
            if (this.wrapperClasses) {
                const container = this.ref(this.prefs().refContainer);

                container.removeClass(this.wrapperClasses);

                this.wrapperClasses = undefined;
            }
        }

        /**
         * @description Before open flyout callback
         * @param flyoutData - flyout data
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        beforeOpenFlyout(flyoutData: object) {
            // just for extending
        }

        /**
         * @description Close Flyout
         */
        closeFlyout() {
            dialogMgr.closeDialog();

            if (this.backFocusElement) {
                this.backFocusElement.focus();
                this.backFocusElement = null;
            }

            this.onAfterCloseFlyout();
        }

        /**
         * @description On after flyout close callback
         */
        onAfterCloseFlyout() {
            this.removeWrapperClasses();

            const flyoutAnimationDuration = 500;

            // Clean up flyout content after animation
            setTimeout(() => {
                const refContainer = this.ref(this.prefs().refContainer);

                this.ref(this.prefs().refContent).empty();
                this.ref(this.prefs().refTitle).empty();
                this.ref(this.prefs().refDialog).attr('aria-label', refContainer.data<string>('labelLoading'));
            }, flyoutAnimationDuration);

            if (this.onAfterCloseListener) {
                this.onAfterCloseListener(this);
                this.onAfterCloseListener = undefined;
            }
        }

        /**
         * @description Cancel Handler
         */
        cancel() {
            super.cancel();

            if (this.onCancelListener) {
                this.onCancelListener(this);
                this.onCancelListener = undefined;
            }
        }
    }

    return GlobalFlyout;
}

export type TGlobalFlyout = ReturnType<typeof GlobalFlyoutClassCreator>;

export type TGlobalFlyoutInstance = InstanceType<TGlobalFlyout>;

export default GlobalFlyoutClassCreator;
